/* eslint-disable max-lines */
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { AgGridReact } from 'ag-grid-react';
import { format, subDays } from 'date-fns';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { listInventoryTransactionsApi } from 'api/inventory';
import DateRangePickerField from 'common/forms/DateRangePickerField';
import { buildFacilityLabelLookup } from 'common/utils/facilityLabel';
import { selectAuthorizedFacilities } from 'store/selectors/facilitySelectors';
import { handleError } from 'store/thunks/errorHandlerThunks';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';

const HEADER_BG = '#0d1f35';
const PAGE_SIZE = 100;

const ALL_TX_TYPES = [
  'DISPENSE',
  'UNDO_DISPENSED',
  'ADJUST_IN',
  'ADJUST_OUT',
  'ADJUST_IN_MANUAL',
  'ADJUST_OUT_MANUAL',
  'TRANSFER_IN',
  'TRANSFER_OUT',
  'ALLOCATED',
  'UNALLOCATED',
];

const TX_TYPE_META = {
  DISPENSE: { label: 'Dispense', bgcolor: '#1565c0', color: 'common.white' },
  UNDO_DISPENSED: { label: 'Undo Dispense', bgcolor: '#2e7d32', color: 'common.white' },
  ADJUST_IN: { label: 'Adjust In', bgcolor: '#388e3c', color: 'common.white' },
  ADJUST_OUT: { label: 'Adjust Out', bgcolor: '#c62828', color: 'common.white' },
  ADJUST_IN_MANUAL: { label: 'Adjust In (Manual)', bgcolor: '#1b5e20', color: 'common.white' },
  ADJUST_OUT_MANUAL: { label: 'Adjust Out (Manual)', bgcolor: '#b71c1c', color: 'common.white' },
  TRANSFER_IN: { label: 'Transfer In', bgcolor: '#00695c', color: 'common.white' },
  TRANSFER_OUT: { label: 'Transfer Out', bgcolor: '#0277bd', color: 'common.white' },
  ALLOCATED: { label: 'Allocated', bgcolor: '#37474f', color: 'common.white' },
  UNALLOCATED: { label: 'Unallocated', bgcolor: '#78909c', color: 'common.white' },
};

const getDefaultDateRange = () => [
  format(subDays(new Date(), 30), 'yyyy-MM-dd'),
  format(new Date(), 'yyyy-MM-dd'),
];

const AG_HEADER_SX = {
  '& .ag-header': { backgroundColor: HEADER_BG },
  '& .ag-header-cell-text': { color: '#fff', fontWeight: 600 },
  '& .ag-header-icon': { color: '#fff' },
  '& .ag-header-cell-resize::after': { backgroundColor: 'rgba(255,255,255,0.45)', width: '2px' },
};

const defaultColDef = {
  resizable: true,
  suppressMovable: false,
  suppressHeaderMenuButton: true,
};

const sideBar = {
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: { suppressRowGroups: true, suppressValues: true, suppressPivotMode: true },
    },
  ],
};

const EmptyLoadingCell = () => null;

// --- Cell renderers ---

const TxTypeBadge = ({ value }) => {
  const meta = TX_TYPE_META[value] ?? { label: value, bgcolor: '#9e9e9e', color: 'common.white' };
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
      }}
    >
      <Chip
        size='small'
        label={meta.label}
        sx={{ bgcolor: meta.bgcolor, color: meta.color, fontWeight: 600, fontSize: 11 }}
      />
    </Box>
  );
};
TxTypeBadge.propTypes = { value: PropTypes.string };
TxTypeBadge.defaultProps = { value: '' };

// `invertPolarity` exists because the two ledger axes read in opposite directions.
//
// On the verified axis, losing stock is the bad case, so negative is red. On the UNVERIFIED axis
// it is the other way round: a positive delta is a NEW unreconciled claim - product dispensed
// that inventory has no receiving record for - and a negative one is a claim being cleared. Left
// uninverted, opening a claim would be painted green as though stock had arrived.
// Exported for test: the grid uses the Enterprise server-side row model, which never issues a
// getRows request in jsdom (no viewport, no height), so cell rendering cannot be reached
// through the modal. The polarity rule below is worth pinning directly.
export const QtyDeltaCell = ({ value, invertPolarity }) => {
  const num = Number.parseFloat(value);
  if (Number.isNaN(num)) return <span>—</span>;
  const isPositive = num > 0;
  const isGood = invertPolarity ? !isPositive : isPositive;
  // Zero is neither a gain nor a loss: a row that touched the bucket and left it level is not a
  // problem, and painting it red reads as one.
  let deltaColor = 'text.secondary';
  if (num !== 0) deltaColor = isGood ? 'success.main' : 'error.main';
  return (
    <Box
      sx={{
        fontWeight: 700,
        color: deltaColor,
        fontFamily: 'monospace',
        display: 'flex',
        alignItems: 'center',
        height: '100%',
        justifyContent: 'flex-start',
      }}
    >
      {isPositive ? `+${num}` : `${num}`}
    </Box>
  );
};
QtyDeltaCell.propTypes = { value: PropTypes.string, invertPolarity: PropTypes.bool };
QtyDeltaCell.defaultProps = { value: null, invertPolarity: false };

const CreatedAtCell = ({ value }) => {
  if (!value) return <span>—</span>;
  const d = new Date(value);
  return (
    <Box sx={{ lineHeight: 1.4, py: 0.5 }}>
      <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
        {format(d, 'yyyy-MM-dd')}
      </Typography>
      <Typography variant='caption' sx={{ color: 'text.secondary' }}>
        {format(d, 'HH:mm:ss')}
      </Typography>
    </Box>
  );
};
CreatedAtCell.propTypes = { value: PropTypes.string };
CreatedAtCell.defaultProps = { value: null };

// --- Main component ---

const TransactionHistoryModal = ({
  open,
  onClose,
  ndc,
  drugName,
  manufacturer,
  fulfillingPharmacyId,
  pharmacyName,
  quantityOnHand,
}) => {
  const gridRef = useRef(null);
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const pharmacyList = useSelector(selectAuthorizedFacilities);
  // Name plus state code: two of these sites read alike by name alone.
  const pharmacyMap = useMemo(() => buildFacilityLabelLookup(pharmacyList), [pharmacyList]);

  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [lotNumber, setLotNumber] = useState('');
  const [dateRange, setDateRange] = useState(getDefaultDateRange);
  const [appliedFilter, setAppliedFilter] = useState(null);
  const [typesAnchorEl, setTypesAnchorEl] = useState(null);

  // Refs so the datasource always reads current values at call time — avoids stale closures.
  const ndcRef = useRef(ndc);
  const pharmacyIdRef = useRef(fulfillingPharmacyId);
  const activeFilterRef = useRef({
    dateFrom: getDefaultDateRange()[0],
    dateTo: getDefaultDateRange()[1],
  });

  useEffect(() => {
    ndcRef.current = ndc;
  }, [ndc]);
  useEffect(() => {
    pharmacyIdRef.current = fulfillingPharmacyId;
  }, [fulfillingPharmacyId]);

  // Stable datasource — never recreated, always reads from refs at call time.
  const datasource = useMemo(
    () => ({
      getRows: (params) => {
        const { startRow, endRow } = params.request;
        setLoading(true);
        listInventoryTransactionsApi({
          ndc: ndcRef.current,
          fulfillingPharmacyId: pharmacyIdRef.current ?? undefined,
          limit: endRow - startRow,
          offset: startRow,
          filter: activeFilterRef.current,
        })
          .then(({ transactions, total: t }) => {
            setTotal(t ?? 0);
            params.success({ rowData: transactions ?? [], rowCount: t ?? 0 });
          })
          .catch((error) => {
            enqueueSnackbar('Failed to load transactions. Please try again.', { variant: 'error' });
            dispatch(handleError({ error }));
            params.fail();
          })
          .finally(() => setLoading(false));
      },
    }),
    [dispatch, enqueueSnackbar]
  );

  const refreshGrid = useCallback(() => {
    gridRef.current?.api?.refreshServerSide({ purge: true });
  }, []);

  useEffect(() => {
    if (open && ndc) {
      ndcRef.current = ndc;
      pharmacyIdRef.current = fulfillingPharmacyId;
      const initialRange = getDefaultDateRange();
      setSelectedTypes([]);
      setLotNumber('');
      setDateRange(initialRange);
      setTotal(0);
      const filter = { dateFrom: initialRange[0], dateTo: initialRange[1] };
      activeFilterRef.current = filter;
      setAppliedFilter(filter);
      refreshGrid();
    }
  }, [open, ndc, fulfillingPharmacyId, refreshGrid]);

  const handleApplyFilters = () => {
    const filter = {
      ...(selectedTypes.length > 0 && { transactionTypes: selectedTypes }),
      ...(lotNumber.trim() && { lotNumber: lotNumber.trim() }),
      ...(dateRange?.[0] && { dateFrom: dateRange[0] }),
      ...(dateRange?.[1] && { dateTo: dateRange[1] }),
    };
    activeFilterRef.current = filter;
    setAppliedFilter(filter);
    refreshGrid();
  };

  const handleReset = () => {
    const freshRange = getDefaultDateRange();
    setSelectedTypes([]);
    setLotNumber('');
    setDateRange(freshRange);
    const filter = { dateFrom: freshRange[0], dateTo: freshRange[1] };
    activeFilterRef.current = filter;
    setAppliedFilter(filter);
    refreshGrid();
  };

  const handleExportCsv = () => {
    gridRef.current?.api?.exportDataAsCsv({
      fileName: `transactions-${ndc ?? 'export'}.csv`,
    });
  };

  const columnDefs = useMemo(
    () => [
      {
        field: 'createdTimestamp',
        headerName: 'Created At',
        minWidth: 130,
        cellRenderer: CreatedAtCell,
        sortable: true,
      },
      {
        field: 'transactionType',
        headerName: 'Type',
        minWidth: 160,
        cellRenderer: TxTypeBadge,
        cellStyle: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
      },
      ...(!fulfillingPharmacyId
        ? [
            {
              field: 'fulfillingPharmacyId',
              headerName: 'Facility',
              minWidth: 160,
              valueFormatter: ({ value }) => pharmacyMap[value] ?? value ?? '—',
              sortable: true,
            },
          ]
        : []),
      {
        field: 'lotNumber',
        headerName: 'Lot #',
        minWidth: 120,
        valueFormatter: ({ value }) => value ?? '—',
        cellStyle: { fontFamily: 'monospace' },
        sortable: true,
      },
      {
        field: 'orderId',
        headerName: 'Order ID',
        minWidth: 140,
        valueFormatter: ({ value }) => value ?? '—',
        cellStyle: { fontFamily: 'monospace' },
        sortable: true,
      },
      {
        field: 'rxNumber',
        headerName: 'Rx Number',
        minWidth: 130,
        valueFormatter: ({ value }) => value ?? '—',
        cellStyle: { fontFamily: 'monospace' },
        sortable: true,
      },
      {
        field: 'productId',
        headerName: 'Product ID',
        minWidth: 140,
        // The prescription override's active productId for this dispense — not the inventory
        // master's product for the row's NDC, which is constant across every row here.
        valueFormatter: ({ value }) => value ?? '—',
        cellStyle: { fontFamily: 'monospace' },
        sortable: true,
      },
      {
        field: 'quantityChange',
        headerName: 'Qty Change',
        minWidth: 120,
        cellRenderer: QtyDeltaCell,
        valueFormatter: ({ value }) => {
          const num = Number.parseFloat(value);
          if (Number.isNaN(num)) return '—';
          return num > 0 ? `+${Number(num).toString()}` : `${Number(num).toString()}`;
        },
        sortable: true,
      },
      {
        field: 'quantityAfter',
        headerName: 'Item Balance After',
        minWidth: 150,
        valueFormatter: ({ value }) => (value != null ? Number(value).toString() : '—'),
        sortable: true,
      },
      // LOT-scoped, unlike Item Balance After beside it — hence the name. WIP only exists per
      // lot (an allocation points at a lot), so this is that lot's allocated-not-yet-dispensed
      // quantity, and the item's total WIP is what these come to across its lots. Read as an
      // item figure it looks wrong on sight: a lot showing 20 against an item holding 200.
      {
        field: 'wipQtyAfter',
        headerName: 'Lot WIP After',
        minWidth: 130,
        valueFormatter: ({ value }) => (value != null ? Number(value).toString() : '—'),
        sortable: true,
      },
      // The unverified axis. Shown by default rather than hidden behind the Columns panel: a
      // claim writes 0 to Qty Change by design (nothing verified moved), so without these two
      // columns those rows are unreadable and the reader is left guessing from a truncated note.
      {
        field: 'unverifiedQtyChange',
        headerName: `${SYSTEM_DEFICIT} Change`,
        minWidth: 155,
        cellRenderer: QtyDeltaCell,
        cellRendererParams: { invertPolarity: true },
        valueFormatter: ({ value }) => {
          const num = Number.parseFloat(value);
          if (Number.isNaN(num)) return '—';
          return num > 0 ? `+${Number(num).toString()}` : `${Number(num).toString()}`;
        },
        sortable: true,
      },
      {
        field: 'unverifiedQtyAfter',
        headerName: `${SYSTEM_DEFICIT} Balance`,
        minWidth: 155,
        // Null, not zero, for a movement that leaves the bucket alone and for every row written
        // before the POM-6089 migration. An em dash says "not applicable"; a 0 would claim the
        // lot has no outstanding claim, which is a different and possibly false statement.
        valueFormatter: ({ value }) => (value != null ? Number(value).toString() : '—'),
        sortable: true,
      },
      {
        field: 'createdBy',
        headerName: 'Created By',
        minWidth: 150,
        valueFormatter: ({ value }) => {
          if (!value) return '—';
          const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');
          const name = `${cap(value.firstName)} ${cap(value.lastName)}`.trim();
          return name || value.pomUserId || '—';
        },
      },
      {
        field: 'notes',
        headerName: 'Notes',
        flex: 1,
        minWidth: 200,
        tooltipField: 'notes',
        valueFormatter: ({ value }) => value ?? '—',
      },
    ],
    [fulfillingPharmacyId, pharmacyMap]
  );

  const activeDateLabel =
    appliedFilter?.dateFrom && appliedFilter?.dateTo
      ? `${appliedFilter.dateFrom} → ${appliedFilter.dateTo}`
      : '';

  if (!ndc) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xl' fullWidth>
      {/* Header */}
      <Box
        sx={{
          bgcolor: HEADER_BG,
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant='caption'
            sx={{
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'uppercase',
              letterSpacing: 1,
              display: 'block',
            }}
          >
            Transaction History
          </Typography>
          <Typography variant='h6' sx={{ color: '#fff', fontWeight: 700, mt: 0.5 }}>
            {drugName}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mt: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Typography
                variant='caption'
                sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: 0.5 }}
              >
                NDC
              </Typography>
              <Typography
                variant='body2'
                sx={{ color: '#fff', fontFamily: 'monospace', fontWeight: 600 }}
              >
                {ndc}
              </Typography>
            </Box>
            {manufacturer && (
              <>
                <Typography sx={{ color: 'rgba(255,255,255,0.25)', lineHeight: 1 }}>|</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography
                    variant='caption'
                    sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    MANUFACTURER
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#fff' }}>
                    {manufacturer}
                  </Typography>
                </Box>
              </>
            )}
            {pharmacyName && (
              <>
                <Typography sx={{ color: 'rgba(255,255,255,0.25)', lineHeight: 1 }}>|</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography
                    variant='caption'
                    sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    FACILITY
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#fff' }}>
                    {pharmacyName}
                  </Typography>
                </Box>
              </>
            )}
            {quantityOnHand != null && (
              <>
                <Typography sx={{ color: 'rgba(255,255,255,0.25)', lineHeight: 1 }}>|</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography
                    variant='caption'
                    sx={{ color: 'rgba(255,255,255,0.5)', fontWeight: 700, letterSpacing: 0.5 }}
                  >
                    CURRENT ON HAND
                  </Typography>
                  <Typography variant='body2' sx={{ color: '#fff', fontWeight: 700 }}>
                    {Number(quantityOnHand).toString()}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Filters */}
        <Box sx={{ px: 3, pt: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {/* Transaction Type */}
            <Box sx={{ minWidth: 200 }}>
              <Typography
                variant='caption'
                sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}
              >
                TRANSACTION TYPE
              </Typography>
              <Select
                multiple
                size='small'
                value={selectedTypes}
                onChange={(e) => setSelectedTypes(e.target.value)}
                input={<OutlinedInput size='small' />}
                renderValue={(selected) =>
                  selected.length === 0
                    ? 'All types'
                    : `${selected.length} type${selected.length > 1 ? 's' : ''} selected`
                }
                displayEmpty
                sx={{ minWidth: 200, height: 35 }}
              >
                {ALL_TX_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={selectedTypes.includes(type)} size='small' />
                    <ListItemText primary={TX_TYPE_META[type]?.label ?? type} />
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {/* Lot # */}
            <Box>
              <Typography
                variant='caption'
                sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}
              >
                LOT #
              </Typography>
              <TextField
                size='small'
                placeholder='e.g. L240518A'
                value={lotNumber}
                onChange={(e) => setLotNumber(e.target.value)}
                sx={{ width: 160 }}
                inputProps={{
                  style: {
                    fontFamily: 'monospace',
                    height: 35,
                    padding: '0 8px',
                    boxSizing: 'border-box',
                  },
                }}
              />
            </Box>

            {/* Date Range */}
            <Box sx={{ minWidth: 240 }}>
              <Typography
                variant='caption'
                sx={{ fontWeight: 600, display: 'block', mb: 0.5, color: 'text.secondary' }}
              >
                DATE RANGE
              </Typography>
              <DateRangePickerField
                onDateChange={(range) => setDateRange(range)}
                initialValue={dateRange}
                maxDate={new Date()}
              />
            </Box>

            {/* Actions */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 2.5 }}>
              <Button
                variant='outlined'
                size='small'
                onClick={handleReset}
                sx={{ minWidth: 100, height: 35 }}
              >
                Reset
              </Button>
              <Button
                variant='contained'
                size='small'
                onClick={handleApplyFilters}
                sx={{ minWidth: 100, height: 35 }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Info bar — active filters summary */}
        <Box
          sx={{
            px: 3,
            pt: 1,
            pb: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            {loading && <CircularProgress size={12} />}

            {/* Count */}
            <Typography variant='body2' sx={{ color: 'text.secondary' }}>
              <strong>{total.toLocaleString()}</strong> transactions
            </Typography>

            {/* Date Range */}
            {activeDateLabel && (
              <>
                <Typography sx={{ color: 'text.disabled', lineHeight: 1 }}>|</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Date Range:
                  </Typography>
                  <Typography variant='caption' sx={{ color: 'text.primary' }}>
                    {activeDateLabel}
                  </Typography>
                </Box>
              </>
            )}

            {/* Lot chip */}
            {appliedFilter?.lotNumber && (
              <>
                <Typography sx={{ color: 'text.disabled', lineHeight: 1 }}>|</Typography>
                <Chip
                  size='small'
                  label={`Lot: ${appliedFilter.lotNumber}`}
                  onDelete={() => {
                    const { lotNumber: _lot, ...rest } = appliedFilter;
                    setLotNumber('');
                    setAppliedFilter(rest);
                    activeFilterRef.current = rest;
                    refreshGrid();
                  }}
                  sx={{ fontFamily: 'monospace', fontSize: 11 }}
                />
              </>
            )}

            {/* Types — clickable summary opens popover */}
            {appliedFilter?.transactionTypes?.length > 0 && (
              <>
                <Typography sx={{ color: 'text.disabled', lineHeight: 1 }}>|</Typography>
                <Box
                  onClick={(e) => setTypesAnchorEl(e.currentTarget)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.75 },
                  }}
                >
                  <Typography variant='caption' sx={{ fontWeight: 700, color: 'text.secondary' }}>
                    Types:
                  </Typography>
                  {(() => {
                    const [first, ...rest] = appliedFilter.transactionTypes;
                    const meta = TX_TYPE_META[first] ?? {
                      label: first,
                      bgcolor: '#9e9e9e',
                      color: '#fff',
                    };
                    return (
                      <>
                        <Chip
                          size='small'
                          label={meta.label}
                          sx={{
                            bgcolor: meta.bgcolor,
                            color: meta.color,
                            fontWeight: 600,
                            fontSize: 11,
                            pointerEvents: 'none',
                          }}
                        />
                        {rest.length > 0 && (
                          <Chip
                            size='small'
                            label={`+${rest.length}`}
                            sx={{ fontWeight: 600, fontSize: 11, pointerEvents: 'none' }}
                          />
                        )}
                      </>
                    );
                  })()}
                </Box>
                {/* Remove-all icon — placed after +N chip */}
                <IconButton
                  size='small'
                  onClick={() => {
                    const { transactionTypes: _t, ...rest } = appliedFilter;
                    setSelectedTypes([]);
                    setAppliedFilter(rest);
                    activeFilterRef.current = rest;
                    refreshGrid();
                    setTypesAnchorEl(null);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <CloseIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                </IconButton>

                {/* Types popover */}
                <Popover
                  open={Boolean(typesAnchorEl)}
                  anchorEl={typesAnchorEl}
                  onClose={() => setTypesAnchorEl(null)}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                >
                  <Box sx={{ p: 2, minWidth: 220 }}>
                    <Typography
                      variant='caption'
                      sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1 }}
                    >
                      APPLIED TYPES
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                      {appliedFilter.transactionTypes.map((type) => {
                        const meta = TX_TYPE_META[type] ?? {
                          label: type,
                          bgcolor: '#9e9e9e',
                          color: '#fff',
                        };
                        return (
                          <Chip
                            key={type}
                            size='small'
                            label={meta.label}
                            onDelete={() => {
                              const updated = appliedFilter.transactionTypes.filter(
                                (t) => t !== type
                              );
                              const updatedFilter =
                                updated.length > 0
                                  ? { ...appliedFilter, transactionTypes: updated }
                                  : { ...appliedFilter, transactionTypes: undefined };
                              setSelectedTypes(updated);
                              setAppliedFilter(updatedFilter);
                              activeFilterRef.current = updatedFilter;
                              refreshGrid();
                              if (updated.length === 0) setTypesAnchorEl(null);
                            }}
                            sx={{
                              bgcolor: meta.bgcolor,
                              color: meta.color,
                              fontWeight: 600,
                              '& .MuiChip-deleteIcon': {
                                color: 'rgba(255,255,255,0.7)',
                                '&:hover': { color: '#fff' },
                              },
                            }}
                          />
                        );
                      })}
                    </Box>
                    <Button
                      size='small'
                      variant='outlined'
                      color='error'
                      fullWidth
                      onClick={() => {
                        const { transactionTypes: _t, ...rest } = appliedFilter;
                        setSelectedTypes([]);
                        setAppliedFilter(rest);
                        activeFilterRef.current = rest;
                        refreshGrid();
                        setTypesAnchorEl(null);
                      }}
                    >
                      Clear All
                    </Button>
                  </Box>
                </Popover>
              </>
            )}
          </Box>

          <Button
            size='small'
            variant='text'
            startIcon={<FileDownloadIcon fontSize='small' />}
            onClick={handleExportCsv}
            sx={{ fontSize: 12 }}
          >
            Export CSV
          </Button>
        </Box>

        {/* Grid */}
        <Box sx={{ height: '52vh', width: '100%', px: 2, position: 'relative', ...AG_HEADER_SX }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 40,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1.5,
                bgcolor: 'rgba(255,255,255,0.88)',
                zIndex: 10,
              }}
            >
              <CircularProgress size={36} thickness={4} />
              <Typography variant='caption' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Loading transactions...
              </Typography>
            </Box>
          )}
          <AgGridReact
            ref={gridRef}
            rowModelType='serverSide'
            cacheBlockSize={PAGE_SIZE}
            serverSideDatasource={datasource}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            sideBar={sideBar}
            loadingCellRenderer={EmptyLoadingCell}
            enableCellTextSelection
            tooltipShowDelay={300}
            rowHeight={52}
            headerHeight={40}
            overlayNoRowsTemplate='<span>No transactions found for the selected filters.</span>'
          />
        </Box>

        {/* Footer */}
        <Box
          sx={{
            px: 3,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: '#fafafa',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <LockIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
            <Typography variant='caption' sx={{ color: 'text.disabled' }}>
              This ledger is append-only. Records cannot be modified or deleted.
            </Typography>
          </Box>
          <Typography variant='caption' sx={{ color: 'text.secondary' }}>
            {total.toLocaleString()} transactions
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

TransactionHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  ndc: PropTypes.string,
  drugName: PropTypes.string,
  manufacturer: PropTypes.string,
  fulfillingPharmacyId: PropTypes.string,
  pharmacyName: PropTypes.string,
  quantityOnHand: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TransactionHistoryModal.defaultProps = {
  ndc: null,
  drugName: '',
  manufacturer: '',
  fulfillingPharmacyId: null,
  pharmacyName: '',
  quantityOnHand: null,
};

export default TransactionHistoryModal;
